import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import * as stylex from "@stylexjs/stylex";
import { cn } from "@/lib/utils";
const cardVariants = cva("text-card-foreground group/card flex flex-col overflow-hidden text-sm ring-1 ring-border/40 backdrop-blur-md has-data-[slot=card-footer]:pb-0 has-[>img:first-child]:pt-0 *:[img:first-child]:rounded-t-[1.5rem] *:[img:last-child]:rounded-b-[1.5rem]", {
  variants: {
    variant: {
      default: "soft-panel",
      ghost: "bg-transparent ring-0 backdrop-blur-none",
      outline: "border border-border bg-transparent",
      elevated: "soft-panel shadow-lg shadow-primary/5 hover:shadow-xl hover:shadow-primary/10 transition-shadow duration-300",
      interactive: "soft-panel cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/10 active:translate-y-0"
    },
    size: {
      default: "gap-4 rounded-[1.5rem] py-5",
      sm: "gap-3 rounded-xl py-3",
      lg: "gap-6 rounded-[2rem] py-7"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
type StyleXOverride = {
  stylexStyle?: stylex.StyleXStyles;
};
interface CardProps extends React.ComponentProps<"div">, VariantProps<typeof cardVariants>, StyleXOverride {}
function Card({
  className,
  variant,
  size,
  stylexStyle,
  style,
  ...props
}: CardProps) {
  const stylexProps = stylex.props(stylexStyle);
  return <div data-slot="card" data-size={size} className={cn(cardVariants({
    variant,
    size,
    className
  }), stylexProps.className)} style={{
    ...stylexProps.style,
    ...style
  }} {...props} />;
}
function CardHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return <div data-slot="card-header" className={cn("group/card-header @container/card-header grid auto-rows-min items-start gap-1.5 rounded-t-[1.5rem] px-5 has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-4 group-data-[size=sm]/card:px-3 group-data-[size=sm]/card:[.border-b]:pb-3 group-data-[size=lg]/card:px-7", className)} {...props} />;
}
function CardTitle({
  className,
  stylexStyle,
  style,
  ...props
}: React.ComponentProps<"div"> & StyleXOverride) {
  const stylexProps = stylex.props(stylexStyle);
  return <div data-slot="card-title" className={cn("text-[1.35rem] leading-tight font-semibold tracking-[-0.03em] group-data-[size=sm]/card:text-base", className, stylexProps.className)} style={{
    ...stylexProps.style,
    ...style
  }} {...props} />;
}
function CardDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return <div data-slot="card-description" className={cn("max-w-3xl text-sm leading-6 text-muted-foreground", className)} {...props} />;
}
function CardAction({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return <div data-slot="card-action" className={cn("col-start-2 row-span-2 row-start-1 self-start justify-self-end", className)} {...props} />;
}
function CardContent({
  className,
  stylexStyle,
  style,
  ...props
}: React.ComponentProps<"div"> & StyleXOverride) {
  const stylexProps = stylex.props(stylexStyle);
  return <div data-slot="card-content" className={cn("px-5 group-data-[size=sm]/card:px-3 group-data-[size=lg]/card:px-7", className, stylexProps.className)} style={{
    ...stylexProps.style,
    ...style
  }} {...props} />;
}
function CardFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return <div data-slot="card-footer" className={cn("flex items-center rounded-b-[1.5rem] border-t bg-muted/45 p-5 group-data-[size=sm]/card:p-3 group-data-[size=lg]/card:p-7", className)} {...props} />;
}
export { Card, cardVariants, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };